// The Scenarios tab (Lane 1344, UI redesign slice 2, plan 5.7): up to six scenarios
// side by side first, then the chosen one (its write-up, who gets what, its parts).
// Every figure comes from the server's comparison read, which reads only saved
// results; a partial or unproven answer says so under its cost and never counts as
// the cheapest (plan 4.5). New scenarios come from the chat (propose, then yes).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ScenarioResult from './ScenarioResult';
import { HistoryMark } from './SupplierHistory';
import { categoryLabeler } from './scenarioText';
import WorkspaceTable from './WorkspaceTable';
import useAutoRecalculate from './useAutoRecalculate';

const KEY = (id) => `srce-compare-${id}`;
const STATE_TEXT = { not_calculated: 'Not calculated', calculating: 'Calculating…', out_of_date: 'Out of date',
  cannot_be_done: 'Cannot be done', failed: 'The calculation failed' };

function readIds(eventId) {
  try { const v = JSON.parse(window.localStorage.getItem(KEY(eventId))); return Array.isArray(v) ? v : null; } catch (e) { return null; }
}

function Change({ text, ticks, lines }) {
  if (text === null || text === undefined) return <span className="srce-mut">-</span>;
  const cls = ticks > 0 ? 'srce-good' : ticks < 0 ? 'srce-neg' : '';
  const shown = ticks < 0 ? `(${text.replace('-', '')})` : text;
  return (
    <span className={lines ? 'srce-mut' : cls} title={lines ? `On ${lines.known} of ${lines.of} parts: the others have no price today` : undefined}>
      {shown}{lines ? ` on ${lines.known} of ${lines.of}` : ''}</span>
  );
}

function coverageText(c) {
  // An earlier figure keeps its coverage and proof words (Codex Lane 1393 r2).
  if (c.state !== 'done' && !c.earlier) return null;
  const parts = (n) => `${n} ${n === 1 ? 'part' : 'parts'}`;
  const missing = [];
  if (c.unallocatable_lines) missing.push(`${parts(c.unallocatable_lines)} could not be awarded`);
  if (c.not_calculated_lines) missing.push(`${parts(c.not_calculated_lines)} not calculated`);
  const out = [c.complete ? 'Complete' : `Partial: ${missing.join(', ')}`];
  if (!c.proven) out.push('best found, not proven best');
  return out.join('; ');
}

export default function ScenarioCompareTab({ api, eventId, summary, options, refreshKey, onAsk, onOpenRules, focus, onAward }) {
  const [ids, setIds] = useState(() => readIds(eventId));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [chosen, setChosen] = useState(null);
  // The chosen scenario's result read, kept with the result id it belongs to, so a
  // switch never shows another scenario's lines (Codex UI slice 2 round 1).
  const [result, setResult] = useState(null);       // { rid, data } or { rid, error }
  const [resultTick, setResultTick] = useState(0);
  const pendingFocus = useRef(null);
  const [show, setShow] = useState({ who: true, parts: false });
  const [details, setDetails] = useState(false);
  // The confirmed evaluation rules Wingman's recommendation follows, with where each
  // came from (a chat request shows "From the chat"; Lane 1395 QA, Ryan).
  const [recRules, setRecRules] = useState(null);
  // Hand moves (slice 2b): the ticked lines and where they go.
  const [picked, setPicked] = useState(() => new Set());
  const [pickedUnits, setPickedUnits] = useState({});     // line -> the units a move takes
  const [moveTo, setMoveTo] = useState('');
  const [linesTick, setLinesTick] = useState(0);
  const [names, setNames] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const currency = (summary.terms && summary.terms.currency) || 'USD';

  const load = useCallback(() => api.scenarioComparison(eventId, ids)
    .then((d) => { setData(d); setError(''); return d; })
    .catch((e) => {
      // A scenario kept in this browser no longer exists: show the default set.
      if (e.status === 404 && ids) { setIds(null); return null; }
      setError(e.message); return null;
    }), [api, eventId, ids]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    api.suppliers(eventId).then((s) => {
      setSuppliers(s.suppliers);
      setNames(Object.fromEntries(s.suppliers.map((x) => [String(x.supplier_id), x.supplier_name])));
    }).catch(() => {});
  }, [api, eventId, refreshKey]);
  const saveIds = (next) => {
    setIds(next);
    try { window.localStorage.setItem(KEY(eventId), JSON.stringify(next)); } catch (e) { /* a convenience */ }
  };

  // The chat made or changed a scenario: show it in the comparison and choose it, once
  // the comparison has loaded (the tab may open with the focus; Codex round 1).
  useEffect(() => { if (focus && focus.scenario_id) pendingFocus.current = focus; }, [focus]);
  useEffect(() => {
    const f = pendingFocus.current;
    if (!f || !data) return;
    pendingFocus.current = null;
    const shownIds = data.columns.map((c) => c.scenario_id);
    if (!shownIds.includes(f.scenario_id)) saveIds([...shownIds.slice(-(data.max - 1)), f.scenario_id]);
    setChosen(f.scenario_id);
  }, [focus, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // While a calculation runs, look again every 2 seconds.
  const calculating = Boolean(data && data.columns.some((c) => c.state === 'calculating'));
  useEffect(() => {
    if (!calculating) return undefined;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [calculating, data, load]);

  const columns = data ? data.columns : [];
  const current = columns.find((c) => c.scenario_id === chosen) || columns[0] || null;
  const currentId = current ? current.scenario_id : null;
  const resultId = current && current.state === 'done' ? current.result_id : null;
  const cannotId = current && current.state === 'cannot_be_done' ? current.result_id : null;
  useEffect(() => { setDetails(false); }, [currentId]);
  const isRec = Boolean(current && current.kind === 'recommendation');
  useEffect(() => {
    if (!details || !isRec) return undefined;
    let live = true;
    api.evaluationRules(eventId)
      .then((d) => { if (live) setRecRules((d.rules || []).filter((r) => r.state !== 'rejected')); })
      .catch(() => { if (live) setRecRules(null); });
    return () => { live = false; };
  }, [api, eventId, details, isRec, refreshKey]);
  const rid = resultId || cannotId;
  useEffect(() => {
    if (!rid) { setResult(null); return undefined; }
    let live = true;
    api.scenarioResult(eventId, rid, { page: 1 })
      .then((r) => { if (live) setResult({ rid, data: r }); })
      .catch((e) => { if (live) setResult({ rid, error: e.message }); });
    return () => { live = false; };
  }, [api, eventId, rid, refreshKey, resultTick]);
  const fetchLines = useCallback((params) => api.resultLines(eventId, rid, params), [api, eventId, rid]);
  const shown = result && result.rid === rid ? result : null;
  // A result read that is no longer current (another buyer changed a rule since the
  // comparison loaded) is never shown as the answer: the comparison is read again and
  // the column then says "Out of date" (Codex UI slice 2 round 3).
  const stale = Boolean(shown && shown.data && shown.data.current === false && current
    && current.state === 'done');
  // Ticks belong to one result: a new or out-of-date result clears them, so a move never
  // takes quantities other than those shown (Codex slice 2b round 2).
  useEffect(() => { setPicked(new Set()); setPickedUnits({}); setMoveTo(''); }, [currentId, resultId, stale]);
  const resultData = shown && shown.data && !stale ? shown.data : null;
  useEffect(() => { if (stale) load(); }, [stale, load]);
  // The recommendation's summary is written after its calculation: look again until
  // it is written or has failed (then the facts show instead).
  const explaining = Boolean(resultData && resultData.recommendation
    && resultData.recommendation.explanation_status === 'pending');
  useEffect(() => {
    if (!explaining) return undefined;
    const t = setTimeout(() => setResultTick((n) => n + 1), 3000);
    return () => clearTimeout(t);
  }, [explaining, resultData]);

  const act = async (action, input) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.scenarioAction(eventId, action, input);
      await load();
      return r || {};
    } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  };
  // "Move to": the ticked lines, whole, to a supplier, today's supplier at today's price,
  // or back to Wingman's choice. On #1 or #2 the server moves them in a copy.
  const move = async () => {
    if (!current || !resultId || !moveTo || picked.size === 0) return;
    const to = /^\d+$/.test(moveTo) ? Number(moveTo) : moveTo;
    const r = await act('move-lines', { scenario_id: current.scenario_id,
      expected_scenario_revision: current.revision, result_id: resultId,
      line_uids: [...picked], to });
    if (!r) return;
    setPicked(new Set());
    setPickedUnits({});
    setMoveTo('');
    setLinesTick((n) => n + 1);
    const made = r.result || {};
    if (made.copied && made.scenario_id) {
      const shown = (data ? data.columns : []).map((c) => c.scenario_id);
      saveIds([...shown.filter((x) => x !== made.scenario_id).slice(-((data ? data.max : 6) - 1)), made.scenario_id]);
      setChosen(made.scenario_id);
    }
  };
  const example = (() => {
    const a = suppliers[0] ? suppliers[0].supplier_name : 'one supplier';
    const b = suppliers[1] ? suppliers[1].supplier_name : 'another';
    return `For example: split everything between ${a} and ${b}, whichever is cheaper; or leave ${a} out of some parts.`;
  })();
  const askNew = () => onAsk('New scenario: ',
    `Describe the award scenario you want to see.\n\n${example}`);
  const askChange = (c) => onAsk(`Change scenario ${c.number} (${c.name}): `,
    `What do you want to change in scenario ${c.number}, "${c.name}"?`);
  const calculate = (c) => (c.kind === 'recommendation' ? act('recommend', {})
    : act('evaluate', { scenario_id: c.scenario_id, expected_scenario_revision: c.revision }));

  const supplierRows = useMemo(() => (data ? data.supplier_rows : []), [data]);
  // Out-of-date scenarios on screen calculate again on their own (Lane 1393). The
  // recommendation's first calculation starts from the Overview, which knows the bids.
  const autoItems = useMemo(() => (data && !['awarded', 'cancelled'].includes(summary.status)
    ? data.columns.filter((c) => c.state === 'out_of_date')
      .map((c) => ({ scenario_id: c.scenario_id, key: `${c.scenario_id}:${c.revision}:${c.result_id || 'none'}` }))
    : []), [data, summary.status]);
  useAutoRecalculate(api, eventId, autoItems, load);
  // Final events keep their scenarios for review; nothing is calculated any more.
  const final = ['awarded', 'cancelled'].includes(summary.status);
  if (summary.status === 'draft') {
    return <div className="srce-mut">Scenarios compare the offers, so they open once the event is published.</div>;
  }
  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading scenarios…</div>;
  const shownIds = columns.map((c) => c.scenario_id);
  const grid = { gridTemplateColumns: `minmax(150px, 1.3fr) repeat(${Math.max(columns.length, 1)}, minmax(110px, 1fr))` };
  const cell = (c, content, extra = '') => (
    <div key={c.scenario_id} className={`srce-sc-cell srce-num${c.kind === 'recommendation' ? ' srce-sc-rec' : ''}${extra}`}>{content}</div>
  );
  const done = (c) => c.state === 'done';

  return (
    <section className="srce-sc-page">
      <div className="srce-row">
        <div className="srce-menu-wrap">
          <button type="button" className="srce-btn" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            Scenarios shown: {columns.map((c) => c.number).join(', ') || 'none'}</button>
          {menu && (
            <div className="srce-menu" role="dialog" aria-label="Scenarios shown">
              {data.scenarios.map((s) => {
                const on = shownIds.includes(s.scenario_id);
                return (
                  <label key={s.scenario_id} className="srce-fitem">
                    <input type="checkbox" checked={on} disabled={!on && shownIds.length >= data.max}
                      onChange={() => saveIds(on ? shownIds.filter((x) => x !== s.scenario_id) : [...shownIds, s.scenario_id])} />
                    <span className="srce-ell">{s.number}. {s.name}</span>
                  </label>
                );
              })}
              <div className="srce-mut srce-fitem">At most {data.max} at once.</div>
              {(data.removed || []).length > 0 && (
                <>
                  <div className="srce-card-h srce-fitem">Removed scenarios</div>
                  {data.removed.map((s) => (
                    <div key={s.scenario_id} className="srce-fitem">
                      <span className="srce-ell srce-mut">{s.number}. {s.name}</span>
                      <button type="button" className="srce-btn srce-btn--sm srce-push" disabled={busy || final}
                        onClick={() => act('unarchive', { scenario_id: s.scenario_id })}>Put back</button>
                    </div>
                  ))}
                </>
              )}
              <button type="button" className="ll-clr" onClick={() => setMenu(false)}>Done</button>
            </div>
          )}
        </div>
        <span className="srce-push srce-row">
          {onAsk && !final && <button type="button" className="srce-btn srce-btn--primary"
            onClick={askNew}>New scenario</button>}
        </span>
      </div>
      {error && <div className="srce-error" role="alert">{error}</div>}

      <div className="srce-scroll">
        <div className="srce-sc" style={grid} role="table" aria-label="Scenario comparison">
          <div className="srce-sc-cell srce-sc-head" />
          {columns.map((c) => (
            <button key={c.scenario_id} type="button"
              className={`srce-sc-cell srce-sc-head srce-sc-pick${c.kind === 'recommendation' ? ' srce-sc-rec' : ''}${c.scenario_id === currentId ? ' srce-sc-on' : ''}`}
              onClick={() => setChosen(c.scenario_id)} title={c.name}>
              <span className="srce-strong srce-ell">{c.number}. {c.name}</span>
              <span className="srce-sub srce-ell">{c.kind === 'recommendation' ? "Wingman's recommendation" : c.rules_line}
                {c.preferred ? ' · Preferred' : ''}</span>
            </button>
          ))}
          <div className="srce-sc-cell srce-strong">Projected cost</div>
          {columns.map((c) => cell(c, done(c) ? (
            <span className="srce-strong">{c.projected}</span>
          ) : c.earlier ? (
            // The last answer stays on screen while it calculates again (Lane 1393).
            // "Updating" only while a calculation runs; else the button stays, so a failed
            // automatic request can always be asked again (Codex Lane 1393 r1 #1).
            <span className="srce-col" title="From before the latest offers or rule changes">
              <span className="srce-mut">{c.projected}</span>
              {c.state === 'calculating' ? <span className="srce-mut">Updating…</span> : (
                <span className="srce-row"><span className="srce-mut">Earlier figure</span>
                  {!final && <button type="button" className="ll-clr" disabled={busy} onClick={() => calculate(c)}>Recalculate</button>}
                </span>
              )}
            </span>
          ) : (
            <span className="srce-col">
              <span className={c.state === 'cannot_be_done' || c.state === 'out_of_date' ? 'srce-neg' : 'srce-mut'}>
                {c.preferred && c.state === 'out_of_date' ? 'Preferred, out of date' : STATE_TEXT[c.state]}</span>
              {['not_calculated', 'out_of_date', 'failed'].includes(c.state) && !final && (
                <button type="button" className="ll-clr" disabled={busy} onClick={() => calculate(c)}>
                  {c.state === 'not_calculated' ? 'Calculate' : 'Recalculate'}</button>
              )}
            </span>
          )))}
          <div className="srce-sc-cell">Change against today</div>
          {columns.map((c) => cell(c, done(c) ? <Change text={c.change} ticks={c.change_ticks} lines={c.change_lines} />
            : c.earlier ? <span className="srce-mut">{c.change}{c.change_lines ? ` on ${c.change_lines.known} of ${c.change_lines.of}` : ''}</span> : null))}
          <div className="srce-sc-cell srce-sub">Coverage</div>
          {columns.map((c) => cell(c, <span className={(done(c) || c.earlier) && !c.complete ? 'srce-warn' : 'srce-sub'}>
            {coverageText(c)}{c.lowest_complete ? ' · lowest complete cost' : ''}</span>))}
          {supplierRows.map((s) => (
            <React.Fragment key={s.supplier_id}>
              <div className="srce-sc-cell srce-hname" title={s.supplier_name}>
                <span className="srce-ell">{s.supplier_name}</span><HistoryMark supplierId={s.supplier_id} /></div>
              {columns.map((c) => {
                const v = c.suppliers[String(s.supplier_id)];
                return cell(c, v ? <span className={c.earlier ? 'srce-mut' : undefined}>{v.cost} <span className="srce-sub">({v.lines})</span></span> : null);
              })}
            </React.Fragment>
          ))}
          <div className="srce-sc-cell">Stays with today's supplier (no change)</div>
          {columns.map((c) => cell(c, (done(c) || c.earlier) && c.not_awarded.lines
            ? <span className={c.earlier ? 'srce-mut' : undefined}>{c.not_awarded.cost} <span className="srce-sub">({c.not_awarded.lines})</span></span> : null))}
          <div className="srce-sc-cell">Need qualification</div>
          {columns.map((c) => cell(c, done(c) || c.earlier ? <span className={c.earlier ? 'srce-mut' : undefined}>
            {c.need_qualification ? `${c.need_qualification} parts` : 'None'}</span> : null))}
        </div>
      </div>
      <div className="srce-sub">Costs are for the event quantities; today's cost uses the 12-month average price paid.
        Parts count lines (a part at one site). Figures in brackets are parts.</div>

      {current && (
        <section className="srce-card2">
          <div className="srce-card2-h">
            <span className="srce-card2-t">{current.number}. {current.name}</span>
            <span className="srce-sub">{current.kind === 'recommendation' ? "Wingman's recommendation" : current.rules_line}
              {current.preferred ? ' · Preferred' : ''}</span>
            <span className="srce-push srce-row">
              {/* Lane 1395: award this scenario's current answer; the review shows every
                  share and notice first, and says what still blocks. */}
              {onAward && !final && resultId && (
                <button type="button" className="srce-btn srce-btn--primary" onClick={() => onAward(resultId)}>
                  Award this scenario</button>
              )}
              {onAsk && !final && !current.system && (
                <button type="button" className="srce-btn srce-btn--primary" onClick={() => askChange(current)}>Change with Wingman</button>
              )}
              <button type="button" className="srce-btn" disabled={busy || final} onClick={() => act('copy', { scenario_id: current.scenario_id })}>Copy</button>
              {!final && (current.preferred
                ? <button type="button" className="srce-btn" disabled={busy} onClick={() => act('unprefer', { scenario_id: current.scenario_id })}>Unmark preferred</button>
                : <button type="button" className="srce-btn" disabled={busy || current.state !== 'done' || current.proposed_rules > 0}
                  title={current.proposed_rules > 0 ? 'Confirm its proposed rules on the Evaluation rules tab first'
                    : current.state !== 'done' ? 'Calculate it first' : ''}
                  onClick={() => act('prefer', { scenario_id: current.scenario_id, expected_scenario_revision: current.revision, confirm_rules: [] })}>
                  Mark preferred</button>)}
              {!final && !current.system && !current.preferred && (
                <button type="button" className="srce-btn srce-btn--neg" disabled={busy}
                  onClick={async () => {
                    if (await act('archive', { scenario_id: current.scenario_id })) {
                      saveIds(columns.map((c) => c.scenario_id).filter((x) => x !== current.scenario_id));
                      setChosen(null);
                    }
                  }}>Remove</button>
              )}
              <button type="button" className="srce-btn" aria-expanded={details} onClick={() => setDetails(!details)}>
                {details ? 'Hide details' : 'Details'}</button>
            </span>
          </div>
          {current.proposed_rules > 0 && (
            <div className="srce-warn srce-card2-f">Includes {current.proposed_rules} proposed {current.proposed_rules === 1 ? 'rule' : 'rules'}
              {onOpenRules && <> (<button type="button" className="ll-clr" onClick={onOpenRules}>review</button>)</>}.</div>
          )}
          {details && current.details && (
            <div className="srce-card2-f srce-stack">
              {current.details.question && <div className="srce-card-q">{current.details.question}</div>}
              <div className="srce-card-h">What it does</div>
              <ul className="srce-card-list">
                <li>{current.kind === 'recommendation' ? 'The lowest total cost that follows every evaluation rule' : current.rules_line}.</li>
                {/* Every rule it uses now (Codex plan r1 #4: proposed ones count too), with
                    where each came from, so a rule asked for in chat shows here. */}
                {current.kind === 'recommendation' && (recRules || []).map((r) => (
                  <li key={r.rule_id}>Rule {r.number}: {r.sentence} <span className="srce-mut">({r.set_by}{r.state === 'proposed' ? ', proposed' : ''})</span></li>
                ))}
                {current.kind === 'recommendation' && current.earlier && (recRules || []).length > 0 && (
                  <li className="srce-mut">These are the rules now; the figures below are from before the latest change.</li>
                )}
                {(current.details.own_settings || []).map((x, i) => <li key={`o${i}`}>{x}</li>)}
                {current.details.instructions.map((x, i) => <li key={`i${i}`}>{x}</li>)}
                {current.details.later_wins && <li>Where two of these cover the same part, the later one wins.</li>}
              </ul>
              {rid && (
                <ScenarioResult key={rid} api={api} eventId={eventId} resultId={rid} names={names}
                  currency={currency} suppliers={suppliers} labelOf={categoryLabeler(options)} compact />
              )}
            </div>
          )}
          {current.earlier && (
            <div className="srce-mut srce-card2-f" role="status">
              New offers or rule changes arrived. The grey figures are from before them{current.state === 'calculating'
                ? '; Wingman is calculating this scenario again.' : '. Recalculate to update them.'}</div>
          )}
          {current.state === 'done' && (
            <div className="srce-card2-f">
              {current.kind === 'recommendation'
                ? (resultData && resultData.recommendation && resultData.recommendation.explanation) || <span className="srce-mut">
                  {explaining ? 'Writing the summary…' : ''}</span>
                : current.write_up}
            </div>
          )}
          {current.state === 'cannot_be_done' && (
            <div className="srce-card2-f srce-col">
              <span className="srce-neg">These rules cannot all hold together.</span>
              {resultData && resultData.conflicts && (resultData.conflicts.sets || []).length > 0 && (
                <span className="srce-sub">{resultData.conflicts.sets.length} {resultData.conflicts.sets.length === 1 ? 'group' : 'groups'} of rules clash.
                  Open Details to see which.</span>
              )}
              {onAsk && !final && <button type="button" className="srce-btn srce-btn--primary"
                onClick={() => onAsk(`Scenario ${current.number} cannot be done. Suggest a fix: `,
                  `Scenario ${current.number} cannot be done. Tell me what you want to keep, and I will suggest a fix.`)}>Ask Wingman to fix it</button>}
            </div>
          )}
          {current.state === 'done' && (
            <div className="srce-row srce-card2-f">
              <label className="srce-inline"><input type="checkbox" checked={show.who} onChange={(e) => setShow({ ...show, who: e.target.checked })} /> Who gets what</label>
              <label className="srce-inline"><input type="checkbox" checked={show.parts} onChange={(e) => setShow({ ...show, parts: e.target.checked })} /> Parts</label>
            </div>
          )}
          {current.state === 'done' && show.who && (
            <div className="srce-scroll">
              <table className="srce-sc-tbl" aria-label="Who gets what">
                <thead><tr>
                  <th >Supplier</th><th className="srce-num">Parts</th><th className="srce-num">Today's cost</th>
                  <th className="srce-num">New cost</th><th className="srce-num">Change</th><th className="srce-num">Share</th>
                  <th className="srce-num">Need qualification</th></tr></thead>
                <tbody>
                  {supplierRows.filter((s) => current.suppliers[String(s.supplier_id)]).map((s) => {
                    const v = current.suppliers[String(s.supplier_id)];
                    return (
                      <tr key={s.supplier_id}>
                        <td>{s.supplier_name}<HistoryMark supplierId={s.supplier_id} /></td><td className="srce-num">{v.lines}</td><td className="srce-num">{v.today}</td>
                        <td className="srce-num">{v.cost}</td>
                        <td className="srce-num">{v.change}{v.change_lines ? <span className="srce-sub"> on {v.change_lines.known} of {v.change_lines.of}</span> : null}</td>
                        <td className="srce-num">{v.share}%</td><td className="srce-num">{v.need_qualification || ''}</td>
                      </tr>
                    );
                  })}
                  {current.not_awarded.lines > 0 && (
                    <tr><td>Stays with today's supplier (no change)</td><td className="srce-num">{current.not_awarded.lines}</td>
                      <td className="srce-num">{current.not_awarded.cost}</td><td className="srce-num">{current.not_awarded.cost}</td>
                      <td className="srce-num">-</td><td /><td /></tr>
                  )}
                  <tr className="srce-strong"><td>Total</td><td /><td className="srce-num">{current.today}</td>
                    <td className="srce-num">{current.projected}</td>
                    <td className="srce-num"><Change text={current.change} ticks={current.change_ticks} lines={current.change_lines} /></td>
                    <td /><td className="srce-num">{current.need_qualification || ''}</td></tr>
                </tbody>
              </table>
            </div>
          )}
          {shown && shown.error && <div className="srce-error srce-card2-f" role="alert">{shown.error}</div>}
          {current.state === 'done' && show.parts && resultId && (
            <div className="srce-card2-f srce-stack">
              {!final && (
                <div className="srce-row srce-movebar">
                  <span className="srce-mut">{picked.size
                    ? `${picked.size} ${picked.size === 1 ? 'part' : 'parts'} ticked, ${[...picked].reduce((a, k) => a + Number(pickedUnits[k] || 0), 0).toLocaleString()} units. A move takes each whole part.`
                    : 'Tick parts to move them by hand.'}</span>
                  <select className="srce-input srce-push" value={moveTo} aria-label="Move to" disabled={!picked.size || busy}
                    onChange={(e) => setMoveTo(e.target.value)}>
                    <option value="">Move to…</option>
                    {suppliers.filter((x) => x.status !== 'removed').map((x) => (
                      <option key={x.supplier_id} value={String(x.supplier_id)}>{x.supplier_name}</option>
                    ))}
                    <option value="today">Today's supplier, today's price</option>
                    <option value="wingman">Let Wingman choose (undo a hand move)</option>
                  </select>
                  <button type="button" className="srce-btn srce-btn--primary" disabled={!picked.size || !moveTo || busy} onClick={move}>
                    {current.system ? 'Move in a copy' : 'Move'}</button>
                  {picked.size > 0 && <button type="button" className="srce-btn" onClick={() => { setPicked(new Set()); setPickedUnits({}); }}>Clear</button>}
                </div>
              )}
              {current.system && !final && <div className="srce-sub">{current.name} stays as it is: a move makes a copy with your changes.</div>}
              <WorkspaceTable key={`${rid}-${linesTick}`} fetchPage={fetchLines} ariaLabel="Parts in this scenario"
                rowKey={(l) => `${l.line_id}|${l.to}`} selectKey={(l) => l.line_id}
                selectable={!final} selected={picked}
                onSelect={(keys, on, rows) => {
                  setPicked((prev) => {
                    const next = new Set(prev);
                    keys.forEach((k) => { if (on) next.add(k); else next.delete(k); });
                    return next;
                  });
                  setPickedUnits((prev) => {
                    const next = { ...prev };
                    (rows || []).forEach((l) => { next[l.line_id] = l.line_units; });
                    return next;
                  });
                }}
                rowClass={(l) => (l.by_hand ? 'srce-wt-hand' : '')}
                emptyText="No parts match these filters."
                columns={[
                  { key: 'part', label: 'Part', filter: 'part', weight: 1.1 },
                  { key: 'description', label: 'Description', filter: 'description', weight: 2.2, min: 160 },
                  { key: 'site', label: 'Site', filter: 'site', weight: 1.1 },
                  { key: 'units', label: 'Quantity', num: true, weight: 0.8, render: (l) => Number(l.units).toLocaleString() },
                  { key: 'today_price', label: "Today's price", num: true, weight: 0.9 },
                  { key: 'today_supplier_name', label: "Today's supplier", filter: 'today_supplier', weight: 1.5 },
                  { key: 'awarded_to', label: 'Awarded to', filter: 'awarded_to', weight: 1.6,
                    title: (l) => (l.by_hand ? `${l.awarded_to} (moved by hand)` : l.awarded_to),
                    render: (l) => (l.to === null ? <span className="srce-neg">No option</span>
                      : <span>{l.by_hand && <span className="srce-pill srce-pill--hand">By hand</span>} {l.awarded_to}</span>) },
                  { key: 'price', label: 'New price', num: true, weight: 0.9 },
                  { key: 'cost', label: 'New cost', num: true, weight: 0.9 },
                  { key: 'change', label: 'Change', num: true, weight: 0.8 },
                  { key: 'moved', label: 'Set by', filter: 'moved', weight: 1, render: (l) => (l.by_hand ? 'Moved by hand' : 'Calculated') },
                ]} />
            </div>
          )}
        </section>
      )}
    </section>
  );
}
